/**
 * Core data types for the CPU inking pipeline.
 *
 * The pipeline produces strokes from G-buffer data:
 *   GBufferData → FeatureMap → StrokeChain[] → StrokeCurve[] → StyledStroke[]
 */

// ---------------------------------------------------------------------------
// G-Buffer readback
// ---------------------------------------------------------------------------

/** CPU-side G-buffer data read back from GPU render targets. */
export interface GBufferData {
  width: number;
  height: number;
  /** Linearized positive view depth in world units. 0 = background. */
  depth: Float32Array;
  /** View-space normals, 3 floats (x,y,z) per pixel. */
  normals: Float32Array;
  /** Logical object ID per pixel (0 = background). */
  objectIds: Uint32Array;
  /** Scene luminance per pixel [0–1], for lighting-aware styling. */
  luminance: Float32Array;
  /** Resolved per-pixel auto-smooth angle in degrees for the owning object. */
  resolvedAutoSmoothAngles: Float32Array;
}

// ---------------------------------------------------------------------------
// Feature extraction
// ---------------------------------------------------------------------------

/** Feature type — identifies which detector produced an edge. */
export enum FeatureType {
  Silhouette = 0,
  Crease = 1,
  ScreenEdge = 2,
}

/** Silhouette subtype — helps debug which contour source produced a red edge. */
export enum SilhouetteKind {
  None = 0,
  Background = 1,
  ObjectBoundary = 2,
  SelfOcclusion = 3,
}

/** Priority ordering: silhouette > crease > screen edge. */
export const FEATURE_PRIORITY: Record<FeatureType, number> = {
  [FeatureType.Silhouette]: 3,
  [FeatureType.Crease]: 2,
  [FeatureType.ScreenEdge]: 1,
};

/**
 * Per-pixel feature data. Each pixel that is detected as an edge
 * gets a confidence value and a feature type.
 */
export interface FeatureMap {
  width: number;
  height: number;
  /** Edge confidence [0–1] per pixel. 0 = not an edge. */
  confidence: Float32Array;
  /** Dominant feature type per pixel (highest-priority detector that fired). */
  featureType: Uint8Array;
  /** Silhouette owner object ID per pixel. 0 = not a silhouette pixel. */
  silhouetteObjectId: Uint32Array;
  /** Adjacent boundary ID per pixel. 0 = background or not a silhouette pixel. */
  silhouetteNeighborId: Uint32Array;
  /** Silhouette subtype per pixel. 0 = not a silhouette pixel. */
  silhouetteKind: Uint8Array;
  /** Resolved per-pixel auto-smooth angle in degrees for the owning object. */
  resolvedAutoSmoothAngles: Float32Array;
}

// ---------------------------------------------------------------------------
// Stroke construction
// ---------------------------------------------------------------------------

/** A raw polyline chain extracted from the feature map. */
export interface StrokeChain {
  /** Pixel coordinates, interleaved [x0, y0, x1, y1, ...]. */
  points: Float32Array;
  /** Dominant feature type for this chain. */
  featureType: FeatureType;
  /** Average confidence across the chain's pixels. */
  confidence: number;
  /** Per-feature contribution ratios [silhouette, crease, screen]. */
  featureMix: [number, number, number];
  /** Whether this chain should be treated as a closed loop. */
  closed: boolean;
  /** Resolved auto-smooth angle in degrees for silhouette smoothing. */
  resolvedAutoSmoothAngle: number;
}

/** A single cubic Bézier segment. */
export interface BezierSegment {
  /** Control points: [p0x, p0y, cp1x, cp1y, cp2x, cp2y, p1x, p1y]. */
  controls: Float64Array;
}

/** A fitted curve — one or more cubic Bézier segments with continuity. */
export interface StrokeCurve {
  /** Ordered Bézier segments forming the curve. */
  segments: BezierSegment[];
  /** Feature type inherited from the source chain. */
  featureType: FeatureType;
  /** Average confidence inherited from the source chain. */
  confidence: number;
  /** Per-feature contribution ratios [silhouette, crease, screen]. */
  featureMix: [number, number, number];
  /** Whether the curve is closed (first point === last point). */
  closed: boolean;
  /** Resolved auto-smooth angle in degrees for silhouette smoothing. */
  resolvedAutoSmoothAngle: number;
}

// ---------------------------------------------------------------------------
// Styling
// ---------------------------------------------------------------------------

/** Per-vertex styling data evaluated along a stroke curve. */
export interface StrokeStyle {
  /** Half-width in pixels at each sample point. */
  thickness: Float32Array;
  /** Opacity [0–1] at each sample point. */
  opacity: Float32Array;
}

/** A fully styled stroke ready for rasterization. */
export interface StyledStroke {
  curve: StrokeCurve;
  style: StrokeStyle;
  /** Sample positions along the curve, interleaved [x0, y0, x1, y1, ...]. */
  samples: Float32Array;
}

// ---------------------------------------------------------------------------
// Pipeline configuration
// ---------------------------------------------------------------------------

/** Parameters controlling the CPU inking pipeline. */
export interface InkingPipelineParams {
  // Feature extraction
  features: {
    silhouette: boolean;
    crease: boolean;
    screenEdges: boolean;
  };
  creaseAngle: number;
  screenEdgeDetail: number;

  // Stroke construction
  simplificationTolerance: number;
  cornerAngle: number;
  minChainLength: number;

  // Styling
  baseThickness: number;
  thicknessMin: number;
  shadowResponse: number;
  shadowExposure: number;
  silhouetteBoost: number;
  distanceNear: number;
  distanceFar: number;
  taperStrength: number;
  baseOpacity: number;
  inkColor: [number, number, number];

  // Light adaptation
  lightRangeMin: number;
  lightRangeMax: number;

  // Camera
  cameraNear: number;
  cameraFar: number;
}

/** Complete output of the CPU inking pipeline. */
export interface InkingPipelineResult {
  strokes: StyledStroke[];
  stats: {
    featurePixels: number;
    chainCount: number;
    curveCount: number;
    totalSegments: number;
    elapsedMs: number;
  };
}
